import { useState } from "react";
import { Field } from "formik";
import { get } from "lodash";
import closeIcon from "../../assets/icons/close-icon.svg";
import { IMAGE_URL } from "../../config";

export const FormMultiImageField = ({
  name = "media",
  label = "Image Upload",
  className = "",
  ...props
}) => {
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [uploadStatuses, setUploadStatuses] = useState([]);

  const handleFileChange = async (event, setFieldValue, values) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    const MAX_SIZE = 100 * 1024 * 1024;
    const validFiles = files.filter(
      (file) => file.size <= MAX_SIZE && file.type.startsWith("image/")
    );

    if (validFiles.length === 0) return;

    const newStatuses = validFiles.map((file, idx) => ({
      id: `${Date.now()}-${idx}`,
      file,
      status: "loading",
      progress: 0,
      url: null,
    }));

    setUploadStatuses((prev) => [...prev, ...newStatuses]);

    const uploadPromises = newStatuses.map(
      (status) =>
        new Promise((resolve) => {
          const formData = new FormData();
          formData.append("file", status.file);

          const xhr = new XMLHttpRequest();

          xhr.upload.addEventListener("progress", (e) => {
            if (e.lengthComputable) {
              const percentComplete = Math.round((e.loaded / e.total) * 100);
              setUploadStatuses((prev) =>
                prev.map((s) =>
                  s.id === status.id ? { ...s, progress: percentComplete } : s
                )
              );
            }
          });

          xhr.onreadystatechange = () => {
            if (xhr.readyState === 4) {
              if (xhr.status >= 200 && xhr.status < 300) {
                const response = JSON.parse(xhr.responseText);
                const url = response.data.filePath;

                setUploadStatuses((prev) =>
                  prev.map((s) =>
                    s.id === status.id
                      ? { ...s, progress: 100, status: "done", url }
                      : s
                  )
                );

                resolve(url);
              } else {
                setUploadStatuses((prev) =>
                  prev.map((s) =>
                    s.id === status.id ? { ...s, status: "error" } : s
                  )
                );
                resolve(null);
              }
            }
          };

          xhr.open(
            "POST",
            IMAGE_URL
          );
          xhr.send(formData);
        })
    );

    const uploadedUrls = await Promise.all(uploadPromises);
    const successfulUrls = uploadedUrls.filter(Boolean);

    const currentMedia = values[name] || [];
    const updatedMedia = [...currentMedia, ...successfulUrls];

    setFieldValue(name, updatedMedia);

    setUploadStatuses((prev) => prev.filter((s) => s.status !== "done"));
  };

  const handleDelete = (index, setFieldValue, values) => {
    const newMedia = [...values[name]];
    newMedia.splice(index, 1);
    setFieldValue(name, newMedia);
  };

  const handleDragStart = (index) => setDraggedIndex(index);
  const handleDragOver = (e) => e.preventDefault();

  const handleDrop = (index, values, setFieldValue) => {
    if (draggedIndex === null || draggedIndex === index) return;
    const updatedItems = [...values[name]];
    const draggedItem = updatedItems.splice(draggedIndex, 1)[0];
    updatedItems.splice(index, 0, draggedItem);
    setFieldValue(name, updatedItems);
    setDraggedIndex(null);
  };

  return (
    <Field name={name}>
      {({ form, meta }) => {
        const values = form.values;
        const mediaItems = get(values, name) || [];

        return (
          <div className={`rounded-lg ${className}`}>
            <div className="flex justify-between items-center my-2">
              <p className="text-lg font-medium">{label}</p>
              {mediaItems.length > 0 && (
                <label className="cursor-pointer text-white bg-primary px-4 py-2 rounded-lg">
                  Add Image
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    className="hidden"
                    onChange={(e) =>
                      handleFileChange(e, form.setFieldValue, values)
                    }
                  />
                </label>
              )}
            </div>
            <div
              className={`my-2 border-[1px] border-lightBg rounded-xl p-2 overflow-x-hidden bg-white ${
                mediaItems?.length > 0
                  ? "h-fit"
                  : "h-[30rem] flex justify-center items-center"
              }`}
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 mt-4">
                {mediaItems.map((url, index) => (
                  <div
                    key={index}
                    data-draggable-index={index}
                    draggable
                    onDragStart={() => handleDragStart(index)}
                    onDragOver={handleDragOver}
                    onDrop={() => handleDrop(index, values, form.setFieldValue)}
                    className="relative w-[12rem] h-[12rem] rounded-lg overflow-hidden border border-lightBg"
                  >
                    <img
                      src={url}
                      alt={`media-${index}`}
                      className="w-full h-full object-cover"
                      width={800}
                      height={600}
                    />
                    <div
                      className="absolute top-2 right-2 bg-white p-[1px] rounded-full shadow cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(index, form.setFieldValue, values);
                      }}
                    >
                      <img src={closeIcon} alt="x" className="w-[20px]" />
                    </div>
                  </div>
                ))}

                {uploadStatuses
                  .filter((status) => status.status !== "done")
                  .map((status) => (
                    <div
                      key={status.id}
                      className="w-[12rem] h-[12rem] rounded-lg relative p-2"
                    >
                      {status.status === "loading" ? (
                        <div className="w-full h-full rounded-lg bg-gray-200 flex flex-col items-center justify-center text-sm text-gray-700 relative p-2">
                          <div className="absolute inset-0 flex items-center justify-center">
                            <p>{status.progress}%</p>
                          </div>
                          <div
                            className="absolute bottom-1 left-0 h-1 bg-primary rounded-lg"
                            style={{ width: `${status.progress}%` }}
                          />
                        </div>
                      ) : (
                        <div className="w-full h-full rounded-lg bg-red-100 flex flex-col items-center justify-center text-red-600 text-sm">
                          <p>Failed</p>
                          <button
                            onClick={() =>
                              setUploadStatuses((prev) =>
                                prev.filter((s) => s.id !== status.id)
                              )
                            }
                            className="mt-2 text-xs text-blue-600 underline"
                          >
                            Remove
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
              </div>

              {uploadStatuses?.length < 1 && mediaItems.length === 0 && (
                <div className="mt-4 text-center bg-[#EDF4F94D] p-6 rounded-lg">
                  <p className="mt-2 text-sm">
                    Drag & Drop or Browse to upload
                  </p>
                  <label className="inline-block mt-4 border border-secondary text-secondary px-4 py-2 rounded cursor-pointer">
                    Browse Files
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      className="hidden"
                      onChange={(e) =>
                        handleFileChange(e, form.setFieldValue, values)
                      }
                    />
                  </label>
                </div>
              )}
            </div>
          </div>
        );
      }}
    </Field>
  );
};
